import React, { RefObject, useEffect, useState, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, YAxis, XAxis, Bar, BarChart, Legend } from 'recharts';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import { fetchCategoriasResumoLista, fetchContacStatus } from '../../../../api/contac/contacAnalyticsApi';
import ModalCategoriaDre from '../../../../components/modal/contac/categoria-dre/ModalCategoriaDre';
import ModalReportsCategoria from '../../../../components/modal/contac/reports/ModalReportsCategoria';
import type { ModalCategoriaDreProps } from '../../../../components/modal/contac/categoria-dre/ModalCategoriaDre';
import FiltroModal from '../../../../components/modal/contac/filtro/ModalFiltro';
import { toast } from 'react-toastify';
import { mensagemDeErro, notify } from '../../../../shared/feedback/notify';
import Filter from '../../../../img/filter.svg';
import ContacArrowDown from '../../../../img/contac_arrow_down.svg';
import ContacArrowUp from '../../../../img/contac_arrow_up.svg';
import ContacLine from '../../../../img/contac_line.svg';
import ContacDocument from '../../../../img/contac_document.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { DismissableTip } from '../../../../components/tooltip/DismissableTip';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import './DashboardCategoria.css';

interface ContacDashboardProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const ContacDashboard: React.FC<ContacDashboardProps> = ({ sidebarRef }) => {

  const truncateLabel = (text: string, maxLength: number = 15) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength - 1) + '…' : text;
  };

  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string | null>(null);
  const [statusIndisponivel, setStatusIndisponivel] = useState(false);
  // at-64: falha na carga dos números. Gráfico zerado é indistinguível de "não houve
  // movimento no período" — numa tela financeira isso é afirmação de negócio, não ausência
  // de dado. O texto guarda a explicação do backend (o 422 diz QUAL código falta).
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  useEffect(() => {
    // Sem o carimbo de atualizacao o usuario le dado antigo achando que e o mais recente:
    // o aviso fica na propria tela (permanente) alem do toast, que some em segundos.
    fetchContacStatus()
      .then((s: any) => setUltimaAtualizacao(s.ultimaAtualizacao))
      .catch(() => {
        setStatusIndisponivel(true);
        notify.warning('Não foi possível verificar a data da última atualização dos dados.', { toastId: 'contac-status' });
      });
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [loading, setLoading] = useState(false);
  const [dataReceita, setDataReceita] = useState<{ name: string; value: number }[]>([]);
  const [dataQuantidadeTotal, setDataQuantidadeTotal] = useState<{ name: string; value: number }[]>([]);
  const [dataSaida, setDataSaida] = useState<{ name: string; value: number }[]>([]);
  const [dataResultadoCategoria, setDataResultadoCategoria] = useState<{ name: string; value: number }[]>([]);
  const [modalCategoriaOpen, setModalCategoriaOpen] = useState(false);
  const [reportsModalOpen, setReportsModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [periodoExibido, setPeriodoExibido] = useState<string>('');
  const [lastDataInicio, setLastDataInicio] = useState<string | null>(null);
  const [lastDataFim, setLastDataFim] = useState<string | null>(null);

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const [startYear, setStartYear] = useState<number>(defaultYear);
  const [startMonth, setStartMonth] = useState<number>(defaultMonth);
  const [endYear, setEndYear] = useState<number>(defaultYear);
  const [endMonth, setEndMonth] = useState<number>(defaultMonth);
  const [totais, setTotais] = useState({
    entradas: 0,
    saidas: 0,
    resultadoLiquido: 0,
  });
  const [dataResultado, setDataResultado] = useState<ModalCategoriaDreProps['data'] | null>(null);

  const isLoadingRef = useRef(false);

  const formatPeriodo = (sY: number, sM: number, eY: number, eM: number) => {
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    return `${meses[sM - 1]}/${sY} – ${meses[eM - 1]}/${eY}`;
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const formatarValor = (valor: number) => {
    const formatado = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
    return valor < 0 ? `R$ -${formatado.replace('R$', '').trim()}` : formatado;
  };

  const handleFiltrarClick = (
    sY: number = startYear,
    sM: number = startMonth,
    eY: number = endYear,
    eM: number = endMonth
  ) => {
    if (
      sY === undefined || sM === undefined ||
      eY === undefined || eM === undefined
    ) {
      toast.error('Por favor, selecione todos os campos de data.');
      return;
    }

    const startDate = new Date(sY, sM - 1, 1);
    const endDate = new Date(eY, eM - 1, 1);

    if (startDate > endDate) {
      toast.error('A data final não pode ser anterior à data inicial.');
      return;
    }

    const dataInicio = startDate.toISOString().split('T')[0];
    const dataFim = new Date(eY, eM, 0).toISOString().split('T')[0];

    setLastDataInicio(dataInicio);
    setLastDataFim(dataFim);

    setPeriodoExibido(formatPeriodo(sY, sM, eY, eM));
    loadData(dataInicio, dataFim);
  };

  const openReportsModal = () => {
    if (!lastDataInicio || !lastDataFim) {
      toast.error("Antes selecione um período nos filtros.");
      return;
    }
    setReportsModalOpen(true);
  };

  const nomeCurtoCategoria: Record<string, string> = {
    "AUTOMOVEL - ROUBO E FURTO": "Automóvel RF",
    "DIESEL LEVE": "Diesel Leve",
    "MOTO RISCO": "Moto Risco",
    "MOTO APP": "Moto App",
    "APLICATIVO DE MOBILIDADE URBANA": "App Mobilidade",
    "SEM CATEGORIA DEFINIDA": "Sem cadastro / sem categoria",
    "PASSEIO": "Passeio",
    "TAXI": "Táxi",
    "DIVERSOS": "Diversos",
    "MOTOS ESPECIAIS": "Motos Especiais",
    "UTILITARIO": "Utilitário",
    "DIESEL LEVE - ROUBO E FURTO": "Diesel Leve RF",
    "MOTOCICLETA": "Motocicleta"
  };

  type LinhaCompetencia = {
    grupo: string | null;
    linha: string | null;
    competencia: string;
    total_entradas: number;
    total_saidas: number;
    classificacao: string | null;
  };

  type ItemBackend = {
    categoria: string;
    totaisEntrada: number;
    totaisSaida: number;
    resultado: number;
    quantidadeAtivo: number;
    quantidadeInativo: number;
    /** Placas com movimento no período que não existem no cadastro do SGA */
    quantidadeSemCadastro?: number;
    placasAtivas?: number;
    placasPeriodo?: number;
    competencias?: Record<string, LinhaCompetencia[]>;
  };

  const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

  const sumGrupoTotal = (
    competencias: Record<string, LinhaCompetencia[]> | undefined,
    grupoAlvo: string,
    tipo: 'ENTRADA' | 'SAIDA'
  ) => {
    let total = 0;
    for (const linhas of Object.values(competencias ?? {})) {
      for (const l of linhas ?? []) {
        if (l?.grupo !== grupoAlvo) continue;
        if (l?.linha !== 'CLASSIFICACAO') continue;
        total += tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
      }
    }
    return total;
  };

  const buildClassificacoes = (
    competencias: Record<string, LinhaCompetencia[]> | undefined,
    grupoAlvo: string,
    tipo: 'ENTRADA' | 'SAIDA'
  ) => {
    const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
    for (const [mes, linhas] of Object.entries(competencias ?? {})) {
      for (const l of linhas ?? []) {
        if (l?.grupo !== grupoAlvo) continue;
        if (l?.linha !== 'CLASSIFICACAO') continue;
        const key = (l?.classificacao ?? '').trim() || 'Sem classificação';
        const v = tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
        if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
        acc[key].total += v;
        acc[key].porMes[mes] = (acc[key].porMes[mes] ?? 0) + v;
      }
    }
    return Object.values(acc)
      .map((x) => ({
        classificacao: x.classificacao,
        total: x.total,
        porMes: Object.entries(x.porMes).map(([competencia, valor]) => ({ competencia, valor })),
      }))
      .sort((a, b) => b.total - a.total);
  };

  const mapItemToCategoriaDre = (item: ItemBackend) => {
    const receitaBruta = toNum(item.totaisEntrada);
    const despesaBruta = toNum(item.totaisSaida);

    const receitaOperacional = sumGrupoTotal(item.competencias, 'RECEITA OPERACIONAL', 'ENTRADA');
    const outrasReceitas = sumGrupoTotal(item.competencias, 'OUTRAS RECEITAS', 'ENTRADA');

    const despesaOperacional = sumGrupoTotal(item.competencias, 'DESPESAS OPERACIONAIS', 'SAIDA');
    const margemDeContribuicao = sumGrupoTotal(item.competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA');

    const resultadoOperacional = receitaBruta - despesaOperacional;
    const resultado = resultadoOperacional - margemDeContribuicao;

    return {
      categoria: item.categoria,
      receitaBruta,
      receitaOperacional,
      outrasReceitas,
      despesaBruta,
      despesaOperacional,
      margemDeContribuicao,
      resultadoOperacional,
      resultado,
      placasAtivas: toNum(item.placasAtivas),
      placasPeriodo: toNum(item.placasPeriodo),
      detalhamento: {
        receitaMensalidade: 0,
        receitaRateio: outrasReceitas,
        receitaNasajonComPlaca: 0,
        despesaNasajonComPlaca: 0,
        despesaEasy: 0,
        despesaRateio: margemDeContribuicao,
        despesaEasyExcedente: 0,
        despesaRecorrenteExcedente: 0,
        despesaRecorrente: 0,
        resultado,
        classificacoesEntradaPlaca: buildClassificacoes(item.competencias, 'RECEITA OPERACIONAL', 'ENTRADA'),
        classificacoesEntradaRateio: buildClassificacoes(item.competencias, 'OUTRAS RECEITAS', 'ENTRADA'),
        classificacoesSaidaPlaca: buildClassificacoes(item.competencias, 'DESPESAS OPERACIONAIS', 'SAIDA'),
        classificacoesSaidaRateio: buildClassificacoes(item.competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA'),
      },
    };
  };

  const loadData = async (dataInicio: string, dataFim: string) => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setErroCarga(null);

    try {
      const resp = await fetchCategoriasResumoLista({
        dataInicio,
        dataFim,
        page: 1,
        limit: 100,
        order: 'lucro',
      });

      const num = (x: any) => (typeof x === 'number' ? x : Number(x ?? 0)) || 0;
      const safeArr = (a: any) => (Array.isArray(a) ? a : []);

      const categorias = safeArr(resp?.items);
      const categoriasDre = categorias.map((c: any) => mapItemToCategoriaDre(c));

      setTotais({
        entradas: num(resp?.entradasTotais?.valor),
        saidas: num(resp?.despesasTotais?.valor),
        resultadoLiquido: num(resp?.resultadoTotal?.valor),
      });

      const resultadoOrdenado = categorias
        .map((c: any) => ({
          name: nomeCurtoCategoria[c?.categoria] || c?.categoria || 'Sem categoria',
          value: num(c?.resultado),
        }))
        .sort((a, b) => a.value - b.value);

      setDataResultadoCategoria(resultadoOrdenado);

      setDataReceita(
        categorias.map((c: any) => ({
          name: nomeCurtoCategoria[c?.categoria] || c?.categoria || 'Sem categoria',
          value: num(c?.totaisEntrada),
        })),
      );

      setDataSaida(
        categorias.map((c: any) => ({
          name: nomeCurtoCategoria[c?.categoria] || c?.categoria || 'Sem categoria',
          value: num(c?.totaisSaida),
        })),
      );

      setDataQuantidadeTotal(
        categorias.map((c: any) => ({
          name: nomeCurtoCategoria[c?.categoria] || c?.categoria || 'Sem categoria',
          value: num(c?.placasPeriodo),
          placasPeriodo: num(c?.placasPeriodo),
          placasAtivas: num(c?.placasAtivas),
        })),
      );

      setDataResultado({
        periodo: `${dataInicio} até ${dataFim}`,
        resultadoTotal: { valor: categorias.reduce((acc: number, c: any) => acc + num(c?.resultado), 0) },
        entradasTotais: { valor: categorias.reduce((acc: number, c: any) => acc + num(c?.totaisEntrada), 0) },
        despesasTotais: { valor: categorias.reduce((acc: number, c: any) => acc + num(c?.totaisSaida), 0) },
        agregacoes: { porCategoria: categoriasDre },
      } as ModalCategoriaDreProps['data']);
    } catch (e) {
      // at-64: o toast genérico engolia a explicação do backend — o 422 de consultor/regional
      // sem código cadastrado diz exatamente o que falta, e o usuário via só "Erro ao carregar".
      const mensagem = mensagemDeErro(
        e,
        'Não foi possível carregar os dados de categorias. Recarregue a página; se persistir, acione a TI.',
      );
      setErroCarga(mensagem);
      // Zera os números: gráfico com o resultado da consulta anterior (ou zerado) seria lido
      // como o resultado do período pedido.
      setTotais({ entradas: 0, saidas: 0, resultadoLiquido: 0 });
      setDataResultadoCategoria([]);
      setDataReceita([]);
      setDataSaida([]);
      setDataQuantidadeTotal([]);
      setDataResultado(null);
      notify.errorFrom(e, mensagem);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const getNiceTicks = (min: number, max: number) => {
    if (min === max) return [min];
    const range = max - min;
    const roughStep = range / 4;
    const power = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalizedStep = roughStep / power;

    let step;
    if (normalizedStep < 1.5) step = 1 * power;
    else if (normalizedStep < 3) step = 2 * power;
    else if (normalizedStep < 7.5) step = 5 * power;
    else step = 10 * power;

    const ticks = [0];

    // Negative direction
    let val = -step;
    while (val >= min) {
      ticks.unshift(val);
      val -= step;
    }
    if (ticks[0] > min) {
      ticks.unshift(ticks[0] - step);
    }

    // Positive direction
    val = step;
    while (val <= max) {
      ticks.push(val);
      val += step;
    }
    if (ticks[ticks.length - 1] < max) {
      ticks.push(ticks[ticks.length - 1] + step);
    }

    return ticks;
  };

  const yTicks = React.useMemo(() => {
    if (dataResultadoCategoria.length === 0) return [];
    const values = dataResultadoCategoria.map((d) => d.value);
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    return getNiceTicks(min, max);
  }, [dataResultadoCategoria]);

  if (loading) return <FullScreenLoader />;


  const COLORS = [
    "#004A8D", "#00CAFF", "#5800FF", "#00FF63", "#0047FF",
    "#FF8C00", "#107100", "black", "#00FAC8", "#FFCC00",
    "#FF037C", "#00D3FF", "#FF1100",
  ];

  const renderCustomLegend = (type: 'currency' | 'quantity' | 'number' | boolean = 'number') => (props: any) => {
    const { payload } = props;
    const isCurrency = type === 'currency' || type === true;
    const isQuantity = type === 'quantity';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: isMobile ? 0 : 30,
          fontSize: 13,
          gap: isMobile ? '12px' : '0',
          marginTop: isMobile ? 16 : 0,
        }}
      >
        {payload.map((entry: any, index: number) => {
          const val = entry.payload;
          return (
            <div
              key={`legend-item-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: isMobile ? 'auto' : (isQuantity ? 270 : 220),
                marginBottom: 6,
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: entry.color,
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: 'var(--tema-texto, #000)', whiteSpace: 'nowrap' }}>
                  {truncateLabel(entry.value, 18)}
                </span>
              </div>
              <span
                style={{
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 60,
                  textAlign: 'right',
                  marginLeft: 4,
                }}
              >
                {isCurrency
                  ? `R$ ${Number(val.value).toLocaleString('pt-BR', {
                    maximumFractionDigits: 0,
                  })}`
                  : isQuantity
                    ? `${Number(val.placasPeriodo ?? val.payload?.placasPeriodo ?? val.value ?? 0).toLocaleString('pt-BR')} (${Number(val.placasAtivas ?? val.payload?.placasAtivas ?? 0).toLocaleString('pt-BR')} ativas)`
                    : Number(val.value).toLocaleString('pt-BR')}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const dataQuantidade = dataQuantidadeTotal;

  const hasData =
    dataReceita.length > 0 ||
    dataSaida.length > 0 ||
    dataResultadoCategoria.length > 0 ||
    dataQuantidadeTotal.length > 0;

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          O desempenho financeiro da oficina agrupado por <strong>categoria de veículo</strong> (Passeio,
          Motocicleta, Diesel Leve...): <strong>Entradas</strong> (receitas), <strong>Saídas</strong> (despesas) e{' '}
          <strong>Resultado</strong> (a diferença entre as duas) no período escolhido. Os dados vêm do sistema
          financeiro e são consolidados por mês — esta tela lê esse consolidado, não lança nada. Enquanto nenhum
          período for aplicado em <strong>Filtros</strong>, os quadros mostram "Aguardando seleção de período...".
        </p>
      ),
    },
    {
      titulo: 'Período de análise',
      conteudo: (
        <p>
          O período é escolhido por mês e ano de início e fim, em <strong>Filtros</strong>. Só é possível consultar
          a partir de <strong>janeiro de 2025</strong> e até o <strong>fechamento de dois meses atrás</strong> — o
          mês corrente e o anterior ainda não estão consolidados e por isso não aparecem para seleção.
        </p>
      ),
    },
    {
      titulo: 'Os gráficos',
      conteudo: (
        <>
          <p><strong>Distribuição de Quantidades</strong> mostra quantas placas cada categoria teve no período — o número entre parênteses são as placas <strong>ativas</strong>.</p>
          <p><strong>Distribuição de Receitas</strong> e <strong>Distribuição de Saídas</strong> mostram a fatia de cada categoria no total de entradas e de despesas.</p>
          <p><strong>Resultado por Categoria</strong> compara o saldo de cada categoria: barras <strong>laranjas</strong> são resultado positivo, <strong>vermelhas</strong> são negativo.</p>
        </>
      ),
    },
    {
      titulo: 'A categoria "Sem cadastro / sem categoria"',
      conteudo: (
        <>
          <p>
            Essa fatia reúne <strong>duas situações diferentes</strong>: a placa que movimentou valores no
            período mas <strong>não está no cadastro de veículos do SGA</strong>, e a placa que está
            cadastrada mas <strong>sem categoria preenchida</strong>. Nos dois casos não há categoria para
            classificar o valor, então ele cai aqui.
          </p>
          <p>
            <strong>Ter valor nessa fatia é normal.</strong> Veículos de terceiros não são cadastrados no
            SGA — e por isso não têm categoria —, mas geram lançamentos que chegam por outras fontes
            (Nasajon, Easy Assist e demais origens financeiras).
          </p>
          <p>
            A contagem de placas dessa fatia inclui as duas situações. Se contasse só as cadastradas, a
            categoria apareceria com valor em reais e quantidade zero, o que é impossível de interpretar.
          </p>
          <p>
            Para ver quais placas são essas, use o Dashboard de Placas: o filtro{' '}
            <strong>Cadastro no SGA</strong> lista somente as que estão fora do cadastro.
          </p>
        </>
      ),
    },
    {
      titulo: 'Demonstração do Resultado e relatórios',
      conteudo: (
        <p>
          O cartão <strong>Demonstração do Resultado</strong> abre o detalhamento por categoria — receita bruta,
          despesas, margem de contribuição e resultado, linha a linha, com exportação em CSV e XLSX. O botão{' '}
          <strong>Relatórios</strong> gera o resumo por categoria (com escolha das categorias) nos mesmos formatos.
          Os dois exigem um período aplicado.
        </p>
      ),
    },
    {
      titulo: 'O que cada perfil vê',
      conteudo: (
        <p>
          Os números já chegam recortados pelo seu perfil: um <strong>consultor</strong> vê apenas as categorias da
          própria carteira, e um usuário <strong>regional</strong> apenas as da sua região — o recorte é feito pelo
          próprio sistema, não há como escolher ver mais. O perfil Contac vê a oficina inteira. Se a tela não
          carregar dados, o seu cadastro pode estar sem o código de consultor ou de regional — procure um
          administrador.
        </p>
      ),
    },
  ];

  return (
    <div className="Frame1000004012">
      <div className="ConteDo">

        {/* Título */}
        <div className="TTuloRecepcao">
          <div className="ChefeDeOficina">
            Contac - Dashboard de Categorias
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            >
              ?
            </button>
            {periodoExibido && <span className="PeriodoTitulo"> {periodoExibido}</span>}
          </div>
          {ultimaAtualizacao ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-3, #888)', margin: '4px 0 0 0' }}>
              Dados atualizados em: {new Date(ultimaAtualizacao).toLocaleString('pt-BR')}
            </p>
          ) : statusIndisponivel ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-laranja, #b26a00)', margin: '4px 0 0 0' }}>
              Não foi possível verificar a data da última atualização dos dados.
            </p>
          ) : null}
          {erroCarga && (
            <p style={{ fontSize: 13, color: 'var(--tema-texto-vermelho, #b00020)', margin: '8px 0 0 0', fontWeight: 600 }}>
              {erroCarga} Os gráficos abaixo estão zerados por causa dessa falha — não representam o
              resultado do período.
            </p>
          )}
        </div>

        <div className="VisualizarOrdenar">
          <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
            <DismissableTip
              tipId="contac_dashboard_filtros"
              content="Selecione o período desejado para filtrar os dados de receitas, saídas e distribuição."
              position="bottom"
            >
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">Filtros</div>
                </div>
              </div>
            </DismissableTip>
            <DismissableTip
              tipId="contac_dashboard_relatorios"
              content="Gere relatórios detalhados e exportações de dados das categorias selecionadas."
              position="bottom"
            >
              <div className="CtaBig MouseHover" onClick={openReportsModal}>
                <div className="Frame1000003987">
                  <img
                    src={ContacDocumentOrange}
                    alt="Relatórios"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">Relatórios</div>
                </div>
              </div>
            </DismissableTip>
          </div>
        </div>

        <div className="container-superior">
          {/* Bloco de Resumo Financeiro */}
          <div className="Frame1">
            {/* Entradas */}
            <div className="card-financeiro">
              <div className="card-icone">
                <img className='imgContac' src={ContacArrowDown} alt="Seta para baixo" />
              </div>
              <div>
                <div className="card-titulo">Entradas</div>
                <div className="card-valor">
                  {`${formatarValor(totais.entradas ?? 0)}`}
                </div>
              </div>
            </div>

            {/* Saídas */}
            <div className="card-financeiro">
              <div className="card-icone">
                <img className='imgContac' src={ContacArrowUp} alt="Seta para cima" />
              </div>
              <div>
                <div className="card-titulo">Saídas</div>
                <div className="card-valor">
                  {`${formatarValor(totais.saidas ?? 0)}`}
                </div>
              </div>
            </div>

            {/* Resultado */}
            <div className="card-financeiro resultado">
              <div className="card-icone">
                <img className='imgContac' src={ContacLine} alt="Linha" />
              </div>
              <div>
                <div className="card-titulo">Resultado</div>
                <div className="card-valor">
                  {`${formatarValor(totais.resultadoLiquido ?? 0)}`}
                </div>
              </div>
            </div>

            {/* Card Laranja */}
            <DismissableTip
              tipId="contac_dashboard_dre"
              content="Clique aqui para visualizar a Demonstração de Resultado (DRE) detalhada por categorias."
              position="top"
              wrapperStyle={{ flex: '1 1 calc(50% - 10px)', display: 'flex' }}
            >
              <div
                className="card-laranja"
                onClick={() => {
                  if (dataResultado) {
                    setModalCategoriaOpen(true);
                  } else {
                    toast.error("Antes selecione um período nos filtros para visualizar a DRE.");
                  }
                }}
                style={{ width: '100%', margin: 0 }}
              >
                <div className="icone-laranja">
                  <img className='imgContac' src={ContacDocument} alt="Documento" />
                </div>
                <div className="texto-laranja">
                  Demonstração<br />
                  do Resultado
                </div>
              </div>
            </DismissableTip>
          </div>

          {/* Gráfico de Quantidade de Placas */}
          <div className="grafico-pizza">
            {!hasData ? (
              <div className="grafico-placeholder">Aguardando seleção de período...</div>
            ) : (
              <>
                {/* Título acima do gráfico */}
                <DismissableTip
                  tipId="contac_chart_quantidades"
                  content="Exibe a quantidade total de placas do período (e ativas em parênteses) de cada categoria."
                  position="top"
                >
                  <h3 className="titulo-grafico">Distribuição de Quantidades</h3>
                </DismissableTip>

                {/* Container do gráfico */}
                <div style={{ marginBottom: 10 }} />

                <div className="grafico-wrapper">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={dataQuantidade}
                        dataKey="value"
                        nameKey="name"
                        cx="40%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={130}
                        stroke="#fff"
                        strokeWidth={2}
                        isAnimationActive={true}
                        labelLine={false}
                      >
                        {dataQuantidade.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(v, _n, { payload }) => [
                          `${Number(payload?.placasPeriodo ?? payload?.payload?.placasPeriodo ?? v ?? 0).toLocaleString('pt-BR')} (${Number(payload?.placasAtivas ?? payload?.payload?.placasAtivas ?? 0).toLocaleString('pt-BR')} ativas)`,
                          nomeCurtoCategoria[payload?.name] || payload?.name,
                        ]}
                      />

                      <Legend
                        layout={isMobile ? "horizontal" : "vertical"}
                        align={isMobile ? "center" : "right"}
                        verticalAlign={isMobile ? "bottom" : "middle"}
                        content={renderCustomLegend('quantity')}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Linha inferior com dois gráficos adicionais */}
        <div className="linha-inferior">
          {/* Gráfico 1 */}
          <div className="grafico-pizza">
            {!hasData ? (
              <div className="placeholder">Aguardando seleção de período...</div>
            ) : (
              <>
                {/* Título acima do gráfico */}
                <DismissableTip
                  tipId="contac_chart_receitas"
                  content="Mostra a proporção de receitas totais geradas por cada categoria."
                  position="top"
                >
                  <h3 className="titulo-grafico">Distribuição de Receitas</h3>
                </DismissableTip>

                {/* Container do gráfico */}
                <div className="grafico-wrapper">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={dataReceita}
                        dataKey="value"
                        nameKey="name"
                        cx="40%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={130}
                        stroke="#fff"
                        strokeWidth={2}
                        isAnimationActive={true}
                        labelLine={false}
                      >
                        {dataReceita.map((entry, index) => (
                          <Cell key={`cell-receita-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(value, name) => [
                          Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 }),
                          name,
                        ]}
                      />

                      <Legend
                        layout={isMobile ? "horizontal" : "vertical"}
                        align={isMobile ? "center" : "right"}
                        verticalAlign={isMobile ? "bottom" : "middle"}
                        content={renderCustomLegend(true)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* Gráfico 2 */}
          <div className="grafico-pizza">
            {!hasData ? (
              <div className="placeholder">Aguardando seleção de período...</div>
            ) : (
              <>
                {/* Título acima do gráfico */}
                <DismissableTip
                  tipId="contac_chart_saidas"
                  content="Mostra a proporção de despesas totais consumidas por cada categoria."
                  position="top"
                >
                  <h3 className="titulo-grafico">Distribuição de Saídas</h3>
                </DismissableTip>

                {/* Container do gráfico */}
                <div className="grafico-wrapper">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={dataSaida}
                        dataKey="value"
                        nameKey="name"
                        cx="40%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={130}
                        stroke="#fff"
                        strokeWidth={2}
                        isAnimationActive={true}
                        labelLine={false}
                      >
                        {dataSaida.map((entry, index) => (
                          <Cell key={`cell-saida-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(value, name) => [
                          Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 }),
                          name,
                        ]}
                      />

                      <Legend
                        layout={isMobile ? "horizontal" : "vertical"}
                        align={isMobile ? "center" : "right"}
                        verticalAlign={isMobile ? "bottom" : "middle"}
                        content={renderCustomLegend(true)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Gráfico de Barras Vertical */}
        <div className="grafico-barras">
          <DismissableTip
            tipId="contac_chart_resultado"
            content="Apresenta o saldo líquido (Receitas - Despesas) de cada categoria. Barras laranjas são positivas e vermelhas são negativas."
            position="top"
            disabled={dataResultadoCategoria.length === 0}
          >
            <h3 className="titulo-grafico">Resultado por Categoria</h3>
          </DismissableTip>
          <div className="container">
            {dataResultadoCategoria.length === 0 ? (
              <div className="placeholder">Aguardando seleção de período...</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dataResultadoCategoria}
                  margin={{ top: 20, right: 20, left: 20, bottom: 90 }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: 'rgba(0,0,0,0.7)', dy: 16 }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={100}
                    tickMargin={12}
                  />
                  <YAxis
                    width={100}
                    ticks={yTicks.length > 0 ? yTicks : undefined}
                    domain={yTicks.length > 0 ? [yTicks[0], yTicks[yTicks.length - 1]] : ['auto', 'auto']}
                    tick={{ fontSize: 12, fill: 'rgba(0,0,0,0.7)' }}
                    tickFormatter={(val) => {
                      const formatado = new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(Math.abs(val));
                      const cleanVal = formatado.replace('R$', '').trim().replace(/\s/g, '\u00a0');
                      return val < 0 ? `R$\u00a0-${cleanVal}` : `R$\u00a0${cleanVal}`;
                    }}
                  />
                  <Tooltip
                    formatter={(value) =>
                      value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                    }
                  />
                  <Bar
                    dataKey="value"
                    radius={[200, 200, 0, 0]}
                    barSize={50}
                    label={{
                      position: 'top',
                      fontSize: 10,
                      fill: 'rgba(0,0,0,0.7)',
                      formatter: (value: number) =>
                        value.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }),
                    }}
                  >
                    {dataResultadoCategoria.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.value < 0 ? '#FF1100' : '#FF8C00'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {dataResultado && (
          <ModalCategoriaDre
            isOpen={modalCategoriaOpen}
            onClose={() => setModalCategoriaOpen(false)}
            data={dataResultado}
          />
        )}
        <FiltroModal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          sidebarRef={sidebarRef}
          onApplyFilters={(sY, sM, eY, eM) => {
            setStartYear(sY);
            setStartMonth(sM);
            setEndYear(eY);
            setEndMonth(eM);
            handleFiltrarClick(sY, sM, eY, eM);
          }}
          initialStartYear={startYear}
          initialStartMonth={startMonth}
          initialEndYear={endYear}
          initialEndMonth={endMonth}
        />
        <ModalReportsCategoria
          isOpen={reportsModalOpen}
          onClose={() => setReportsModalOpen(false)}
          initialCategoria={[]}
          dataInicio={lastDataInicio!}
          dataFim={lastDataFim!}
        />

        <ComoFunciona
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          titulo="Como Funciona — Dashboard de Categorias"
          secoes={helpSections}
        />
      </div>
    </div>
  );
};

export default ContacDashboard;
